import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import {
  BookOpen,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Filter,
  ChevronDown,
  ChevronUp,
  RotateCcw,
  Calendar,
  Plus,
} from "lucide-react";
import { journalEntriesApi, chartOfAccountsApi } from "../../services/api";
import SearchableSelect from "../ui/SearchableSelect";
import JournalEntryDetailModal from "./JournalEntryDetailModal";
import NewJournalEntryModal from "./NewJournalEntryModal";

interface JournalLine {
  accountId: { _id: string; code: string; name: string; type: string } | string;
  debitCents: number;
  creditCents: number;
  matterId?: any;
  clientId?: any;
  memo?: string | null;
}

interface JournalEntry {
  _id: string;
  postedAt: string;
  postedBy?: { name?: string; email?: string } | null;
  currency: string;
  memo: string | null;
  source: { type: string; id: string };
  lines: JournalLine[];
  reversedByEntryId?: string | null;
}

interface ChartAccount {
  _id: string;
  code: string;
  name: string;
  type: string;
}

function formatCents(cents: number): string {
  if (cents === 0) return "-";
  return new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
  }).format(cents / 100);
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-CA", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function sourceLabel(type: string): string {
  const labels: Record<string, string> = {
    trust_receipt: "Trust Receipt",
    trust_disbursement: "Trust Disbursement",
    invoice: "Invoice",
    payment: "Payment",
    transfer: "Transfer",
    correction: "Correction",
    manual: "Manual",
  };
  return labels[type] || type;
}

const sourceBadge: Record<string, { bg: string; text: string }> = {
  trust_receipt: { bg: "bg-emerald-100", text: "text-emerald-700" },
  trust_disbursement: { bg: "bg-blue-100", text: "text-blue-700" },
  invoice: { bg: "bg-amber-100", text: "text-amber-700" },
  payment: { bg: "bg-purple-100", text: "text-purple-700" },
  transfer: { bg: "bg-slate-100", text: "text-slate-700" },
  correction: { bg: "bg-red-100", text: "text-red-700" },
  manual: { bg: "bg-slate-100", text: "text-slate-700" },
};

const sourceTypeOptions = [
  { value: "", label: "All Sources" },
  { value: "trust_receipt", label: "Trust Receipt" },
  { value: "trust_disbursement", label: "Trust Disbursement" },
  { value: "invoice", label: "Invoice" },
  { value: "payment", label: "Payment" },
  { value: "transfer", label: "Transfer" },
  { value: "correction", label: "Correction" },
  { value: "manual", label: "Manual" },
];

const LIMIT = 20;

const GeneralLedgerTab: React.FC = () => {
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [accounts, setAccounts] = useState<ChartAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  // Filters
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [accountId, setAccountId] = useState("");
  const [sourceType, setSourceType] = useState("");

  // Expanded rows
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Detail modal
  const [detailEntry, setDetailEntry] = useState<JournalEntry | null>(null);
  const [showDetail, setShowDetail] = useState(false);

  // New entry modal
  const [showNewEntry, setShowNewEntry] = useState(false);

  // Load chart of accounts for filter dropdown
  useEffect(() => {
    const loadAccounts = async () => {
      try {
        const res = await chartOfAccountsApi.list();
        const flat = res.data?.flat || [];
        setAccounts(flat.filter((a: any) => a.active !== false));
      } catch (err) {
        console.error("Failed to load chart of accounts:", err);
      }
    };
    loadAccounts();
  }, []);

  const loadEntries = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = { page, limit: LIMIT };
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      if (accountId) params.accountId = accountId;
      if (sourceType) params.sourceType = sourceType;

      const res = await journalEntriesApi.list(params);
      setEntries(res.data?.data || []);
      setTotal(res.data?.pagination?.total || 0);
    } catch (err) {
      console.error("Failed to load journal entries:", err);
    } finally {
      setLoading(false);
    }
  }, [page, dateFrom, dateTo, accountId, sourceType]);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  const totalPages = Math.ceil(total / LIMIT);

  const handleFilter = () => {
    setPage(1);
    loadEntries();
  };

  const toggleExpand = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const openDetail = (entry: JournalEntry) => {
    setDetailEntry(entry);
    setShowDetail(true);
  };

  const accountOptions = [
    { value: "", label: "All Accounts" },
    ...accounts.map((a) => ({
      value: a._id,
      label: `${a.code} - ${a.name}`,
    })),
  ];

  return (
    <div>
      {/* Filter Bar */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="h-4 w-4 text-slate-500" />
          <span className="text-sm font-medium text-slate-700">Filters</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div>
            <label className="block text-xs text-slate-500 mb-1">From Date</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-emerald-600"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">To Date</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-emerald-600"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Account</label>
            <SearchableSelect
              options={accountOptions}
              value={accountId || null}
              onChange={(val) => { setAccountId(val || ""); setPage(1); }}
              placeholder="All Accounts"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Source Type</label>
            <SearchableSelect
              options={sourceTypeOptions}
              value={sourceType || null}
              onChange={(val) => { setSourceType(val || ""); setPage(1); }}
              placeholder="All Sources"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={() => {
                setDateFrom("");
                setDateTo("");
                setAccountId("");
                setSourceType("");
                setPage(1);
              }}
              className="w-full px-4 py-2 rounded-xl border border-slate-200 text-sm text-slate-600 hover:bg-slate-50 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Results Count + New Entry Button */}
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-slate-500">
          {total} journal {total === 1 ? "entry" : "entries"} found
        </p>
        <button
          onClick={() => setShowNewEntry(true)}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold text-white bg-brand hover:bg-brand-hover transition-colors"
        >
          <Plus className="h-4 w-4" />
          New Entry
        </button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
        </div>
      ) : entries.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
          <BookOpen className="h-12 w-12 mx-auto mb-3 text-slate-300" />
          <p className="text-lg font-medium text-slate-600">No journal entries</p>
          <p className="text-sm text-slate-400 mt-1">
            Journal entries are created automatically when trust transactions or payments are recorded.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-white text-left bg-brand">
                  <th className="px-4 py-3 w-8"></th>
                  <th className="px-4 py-3 font-semibold">Date</th>
                  <th className="px-4 py-3 font-semibold">Memo</th>
                  <th className="px-4 py-3 font-semibold">Source</th>
                  <th className="px-4 py-3 font-semibold text-right">Total Debit</th>
                  <th className="px-4 py-3 font-semibold text-right">Total Credit</th>
                  <th className="px-4 py-3 font-semibold">Posted By</th>
                  <th className="px-4 py-3 font-semibold w-8"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {entries.map((entry) => {
                  const totalDebits = entry.lines.reduce(
                    (sum, l) => sum + (l.debitCents || 0),
                    0
                  );
                  const totalCredits = entry.lines.reduce(
                    (sum, l) => sum + (l.creditCents || 0),
                    0
                  );
                  const badge = sourceBadge[entry.source.type] || sourceBadge.manual;
                  const isExpanded = expandedId === entry._id;
                  const postedByName =
                    typeof entry.postedBy === "object" && entry.postedBy
                      ? entry.postedBy.name || entry.postedBy.email || "System"
                      : "System";

                  return (
                    <React.Fragment key={entry._id}>
                      <tr
                        className={`hover:bg-slate-50 cursor-pointer ${
                          entry.reversedByEntryId ? "opacity-60" : ""
                        }`}
                        onClick={() => toggleExpand(entry._id)}
                      >
                        <td className="px-4 py-3">
                          {isExpanded ? (
                            <ChevronUp className="h-4 w-4 text-slate-400" />
                          ) : (
                            <ChevronDown className="h-4 w-4 text-slate-400" />
                          )}
                        </td>
                        <td className="px-4 py-3 text-slate-700 whitespace-nowrap">
                          {formatDate(entry.postedAt)}
                        </td>
                        <td className="px-4 py-3 text-slate-800 font-medium max-w-[250px] truncate">
                          {entry.memo || "-"}
                          {entry.reversedByEntryId && (
                            <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-red-50 text-red-600">
                              <RotateCcw className="h-3 w-3" />
                              Reversed
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}
                          >
                            {sourceLabel(entry.source.type)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-slate-800">
                          {formatCents(totalDebits)}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-slate-800">
                          {formatCents(totalCredits)}
                        </td>
                        <td className="px-4 py-3 text-slate-600 text-xs whitespace-nowrap">
                          {postedByName}
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openDetail(entry);
                            }}
                            className="text-xs text-brand hover:text-brand-hover font-medium"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                      {/* Expanded lines */}
                      {isExpanded && (
                        <tr>
                          <td colSpan={8} className="p-0">
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: "auto" }}
                              exit={{ opacity: 0, height: 0 }}
                              className="bg-slate-50 border-t border-slate-200"
                            >
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="text-slate-500 border-b border-slate-200">
                                    <th className="px-8 py-2 text-left font-medium">Code</th>
                                    <th className="px-4 py-2 text-left font-medium">Account</th>
                                    <th className="px-4 py-2 text-left font-medium">Type</th>
                                    <th className="px-4 py-2 text-right font-medium">Debit</th>
                                    <th className="px-4 py-2 text-right font-medium">Credit</th>
                                    <th className="px-4 py-2 text-left font-medium">Line Memo</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                  {entry.lines.map((line, li) => {
                                    const acct =
                                      typeof line.accountId === "object" && line.accountId
                                        ? line.accountId
                                        : null;
                                    return (
                                      <tr key={li} className="hover:bg-white">
                                        <td className="px-8 py-2 font-mono text-slate-600">
                                          {acct?.code || "-"}
                                        </td>
                                        <td className="px-4 py-2 text-slate-800 font-medium">
                                          {acct?.name || "Unknown"}
                                        </td>
                                        <td className="px-4 py-2 text-slate-500 capitalize">
                                          {acct?.type || "-"}
                                        </td>
                                        <td className="px-4 py-2 text-right font-mono text-slate-800">
                                          {formatCents(line.debitCents || 0)}
                                        </td>
                                        <td className="px-4 py-2 text-right font-mono text-slate-800">
                                          {formatCents(line.creditCents || 0)}
                                        </td>
                                        <td className="px-4 py-2 text-slate-500">
                                          {line.memo || "-"}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </motion.div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
              <p className="text-xs text-slate-500">
                Page {page} of {totalPages}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page <= 1}
                  className="p-1.5 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page >= totalPages}
                  className="p-1.5 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Detail Modal */}
      <JournalEntryDetailModal
        open={showDetail}
        onClose={() => setShowDetail(false)}
        entry={detailEntry}
        onReversed={loadEntries}
      />

      {/* New Journal Entry Modal */}
      <NewJournalEntryModal
        open={showNewEntry}
        onClose={() => setShowNewEntry(false)}
        onCreated={() => {
          setPage(1);
          loadEntries();
        }}
      />
    </div>
  );
};

export default GeneralLedgerTab;
